import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { OperationsContext } from "../AdminPanel/context/OperationsContext";
import "../styles/PriceTable.css";
import Carousel from "./Carousel";
import Marquee from "./Marquee";
import Footer from "./Footer";
import { UserContext } from "../AdminPanel/context/UserContext";

const PriceTable = ({
  marqueeText,
  scrollAmount,
  symbols,
  isStreamOn,
  isFrozen,
}) => {
  // localStorage'dan fiyat verilerini yükle veya varsayılan değerleri kullan
  const getInitialPrices = () => {
    try {
      const savedPrices = localStorage.getItem('priceTablePrices');
      if (savedPrices) {
        const parsed = JSON.parse(savedPrices);
        // Sadece geçerli verileri kullan (son 5 dakika içinde güncellenmiş)
        const now = Date.now();
        if (parsed.timestamp && (now - parsed.timestamp) < 300000) { // 5 dakika
          return parsed.prices || {};
        }
      }
    } catch (error) {
      console.error('LocalStorage fiyat verileri yüklenirken hata:', error);
    }
    return {};
  };

  const [prices, setPrices] = useState(getInitialPrices);
  const { operations } = useContext(OperationsContext);
  const { userSettings } = useContext(UserContext);
  
  // WebSocket bağlantı yönetimi için ref'ler
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const isConnectingRef = useRef(false);
  const intervalRef = useRef(null);
  const lastUpdateTimeRef = useRef(Date.now());
  const symbolsRef = useRef(symbols);
  const isStreamOnRef = useRef(isStreamOn);
  const isFrozenRef = useRef(isFrozen);
  
  // Ref'leri güncelle
  useEffect(() => {
    symbolsRef.current = symbols;
    isStreamOnRef.current = isStreamOn;
    isFrozenRef.current = isFrozen;
  }, [symbols, isStreamOn, isFrozen]);

  // userSettings'ten ayarları al
  const show18Ayar = userSettings?.ayar18 || false;
  const show14Ayar = userSettings?.ayar14 || false;

  // Yerel depolama güncelleme
  useEffect(() => {
    localStorage.setItem('show18Ayar', JSON.stringify(show18Ayar));
    localStorage.setItem('show14Ayar', JSON.stringify(show14Ayar));
  }, [show18Ayar, show14Ayar]);

  // Fiyat verilerini localStorage'a kaydet
  const savePricesToStorage = useCallback((newPrices) => {
    try {
      const dataToSave = {
        prices: newPrices,
        timestamp: Date.now()
      };
      localStorage.setItem('priceTablePrices', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Fiyat verileri localStorage\'a kaydedilirken hata:', error);
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    // Eğer dondurulmuşsa veya stream kapalıysa bağlanma
    if (isFrozenRef.current || !isStreamOnRef.current) {
      return;
    }

    // Eğer zaten bağlanmaya çalışıyorsa veya maksimum deneme sayısını aştıysa, çık
    if (isConnectingRef.current || retryCountRef.current >= 5) {
      return;
    }

    isConnectingRef.current = true;

    try {
      // Önceki bağlantıyı temizle
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      wsRef.current = new WebSocket("wss://sslsocket.aifanet.com/5.250.255.84/24876");

      wsRef.current.onopen = () => {
        console.log("PriceTable WebSocket bağlantısı açıldı");
        retryCountRef.current = 0;
        isConnectingRef.current = false;
        
        // İlk bağlantıda fiyat talebi gönder
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'requestUpdate' }));
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          const filteredData = data.filter((item) =>
            symbolsRef.current.includes(item.Code)
          );

          const newPrices = {};
          let hasValidData = false;

          filteredData.forEach((item) => {
            // Tüm geçerli kodları kabul et
            const bid = parseFloat(item.Bid) || 0;
            const ask = parseFloat(item.Ask) || 0;
            
            // Sadece geçerli fiyat verilerini kabul et
            if (bid > 0 && ask > 0) {
              newPrices[item.Code] = {
                Bid: bid,
                Ask: ask,
              };
              hasValidData = true;
            }
          });

          // Sadece geçerli veriler varsa güncelle
          if (hasValidData) {
            setPrices(prevPrices => {
              const updatedPrices = {
                ...prevPrices,
                ...newPrices
              };
              
              // Fiyat verilerini localStorage'a kaydet
              savePricesToStorage(updatedPrices);
              lastUpdateTimeRef.current = Date.now();
              
              return updatedPrices;
            });
          }
        } catch (error) {
          console.error("WebSocket mesajı ayrıştırılırken hata oluştu:", error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log("PriceTable WebSocket bağlantısı kapandı:", event.code, event.reason);
        isConnectingRef.current = false;
        
        // Sadece beklenmeyen kapanmalarda yeniden bağlan
        if (event.code !== 1000 && retryCountRef.current < 5 && !isFrozenRef.current && isStreamOnRef.current) {
          retryCountRef.current++;
          const delay = Math.min(30000 * Math.pow(2, retryCountRef.current - 1), 300000);
          console.log(`PriceTable yeniden bağlanma denemesi ${retryCountRef.current}/5, ${delay}ms sonra`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else if (retryCountRef.current >= 5) {
          console.error("PriceTable maksimum yeniden bağlanma denemesi aşıldı.");
        }
      };

      wsRef.current.onerror = (error) => {
        console.error("PriceTable WebSocket hatası:", error);
        isConnectingRef.current = false;
        
        // Hata durumunda bağlantıyı kapat
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close();
        }
      };
    } catch (error) {
      console.error("PriceTable WebSocket bağlantısı kurulurken hata:", error);
      isConnectingRef.current = false;
      
      // Bağlantı kurulamazsa yeniden dene
      if (retryCountRef.current < 5 && !isFrozenRef.current && isStreamOnRef.current) {
        retryCountRef.current++;
        const delay = 60000;
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, delay);
      }
    }
  }, [savePricesToStorage]);

  // Ana WebSocket bağlantı yönetimi
  useEffect(() => {
    // İlk bağlantıyı kur
    if (isStreamOn && !isFrozen) {
      connectWebSocket();
    }

    // Periyodik fiyat talebi için interval
    intervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isFrozenRef.current && isStreamOnRef.current) {
        wsRef.current.send(JSON.stringify({ type: 'requestUpdate' }));
      }
    }, 30000); // 30 saniyede bir güncelleme talebi

    return () => {
      // Cleanup
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
      isConnectingRef.current = false;
      retryCountRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Sadece component mount olduğunda çalışır

  // isStreamOn veya isFrozen değiştiğinde bağlantıyı yeniden yönet
  useEffect(() => {
    if (isFrozen || !isStreamOn) {
      // Bağlantıyı kapat
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      isConnectingRef.current = false;
      retryCountRef.current = 0;
    } else {
      // Bağlantıyı yeniden kur
      connectWebSocket();
    }
  }, [isStreamOn, isFrozen, connectWebSocket]);

  // Bağlantı sağlığını kontrol et
  useEffect(() => {
    const healthCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
      
      // Son 2 dakikada güncelleme alınmadıysa bağlantıyı yeniden kur
      if (timeSinceLastUpdate > 120000 && wsRef.current && !isFrozenRef.current && isStreamOnRef.current) {
        console.log('PriceTable: Son güncellemeden bu yana çok zaman geçti, bağlantıyı yeniliyorum');
        wsRef.current.close();
      }
    }, 60000); // Her dakika kontrol et

    return () => {
      clearInterval(healthCheckInterval);
    };
  }, []); // Sadece component mount olduğunda çalışır

  const formatNumber = (number, code) => {
    const numericValue = parseFloat(number); // Sayıyı sayısal değere dönüştür
    if (isNaN(numericValue)) {
      return number; // Sayı değilse, olduğu gibi döndür
    }
  
    // USDTRY ve EURTRY için sabit iki ondalık basamak göster
    if (code === "USDTRY" || code === "EURTRY") {
      return numericValue.toFixed(2);
    }

    // EHAS için tam sayıya yuvarla ve .00 ekle
    if (code === "EHAS") {
      const roundedToInteger = Math.round(numericValue); // Tam sayıya yuvarla
      return roundedToInteger.toFixed(2); // .00 ekle
    }
  
    // Diğer kodlar için 5'in katlarına yuvarla
    const roundedToFive = Math.round(numericValue / 5) * 5; // 5'in katlarına yuvarla
    return roundedToFive.toFixed(2);
  };

  // Operations değiştiğinde fiyatları yeniden render et
  useEffect(() => {
    console.log('🔍 PriceTable: Operations değişikliği algılandı:', operations);
    // Force re-render için bir state güncellemesi
    setPrices(prevPrices => ({ ...prevPrices }));
  }, [operations]);
  

  const getPriceData = (code) => {
    const uhasItem = prices["UHAS"];
    const currencyItem = prices[code];

    if (code === "USDTRY" || code === "EURTRY") {
      const baseBid = parseFloat(currencyItem?.Bid || 0);
      const baseAsk = parseFloat(currencyItem?.Ask || 0);

      const operationBidAdjustment = parseFloat(operations?.[code]?.Bid || 0);
      const operationAskAdjustment = parseFloat(operations?.[code]?.Ask || 0);

      const bid = baseBid + operationBidAdjustment;
      const ask = baseAsk + operationAskAdjustment;

      // Sadece geçerli değerler döndür
      if (bid > 0 && ask > 0) {
        return {
          Bid: formatNumber(bid, code),
          Ask: formatNumber(ask, code),
        };
      }
      
      // Geçerli değer yoksa varsayılan değerler döndür
      return {
        Bid: formatNumber(0, code),
        Ask: formatNumber(0, code),
      };
    }

    if (!uhasItem || !uhasItem.Bid || !uhasItem.Ask) {
      return { Bid: formatNumber(0, code), Ask: formatNumber(0, code) };
    }

    const operationBidAdjustment = parseFloat(operations?.[code]?.Bid || 1);
    const operationAskAdjustment = parseFloat(operations?.[code]?.Ask || 1);

    // EHAS için özel kontrol
    if (code === "EHAS") {
      // eslint-disable-next-line no-unused-vars
      const ehasBid = uhasItem.Bid * operationBidAdjustment;
      const ehasAsk = uhasItem.Ask * operationAskAdjustment;
      
      // Eğer EHAS fiyatı 3455.00 ise, alış fiyatlarını özel değerlerle değiştir
      if (Math.abs(ehasAsk - 3455.00) < 0.01) {
        return {
          Bid: formatNumber(3451, "EHAS"),
          Ask: formatNumber(3455, "EHAS"),
        };
      }
    }

    const calculatedBid = uhasItem.Bid * operationBidAdjustment;
    const calculatedAsk = uhasItem.Ask * operationAskAdjustment;

    // Sadece geçerli değerler döndür
    if (calculatedBid > 0 && calculatedAsk > 0) {
      return {
        Bid: formatNumber(calculatedBid, code),
        Ask: formatNumber(calculatedAsk, code),
      };
    }

    return {
      Bid: formatNumber(0, code),
      Ask: formatNumber(0, code),
    };
  };

  const uhasData = getPriceData("UHAS");

  return (
    <div className="tablo-container container-fluid">
      <div className="row başlık2 text-center d-flex align-items-center justify-content-between">
        <div className="col-sm-8">
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className="col-4 c-left">
              <span className="blinking-dot"></span> CANLI
            </div>
            <div className="col-4">ALIŞ</div>
            <div className="col-4">SATIŞ</div>
          </div>
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-4 p3 h align-left`}>
              <span className="r">24</span> AYAR GR
            </div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(uhasData.Bid, "UHAS")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(uhasData.Ask, "UHAS")}</span>
            </div>
          </div>
          <div className="row boşluk"></div>
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-4 p3 h align-left`}>
              <span className="r">22</span> AYAR
            </div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("22AYAR").Bid, "22AYAR")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("22AYAR").Ask, "22AYAR")}</span>
            </div>
          </div>
          <div className="row boşluk"></div>
          {show18Ayar && (
            <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
              <div className={`col-4 p3 h align-left`}>
                <span className="r">18</span> AYAR
              </div>
              <div className="col-4 p3 bg-light text-dark">
                <span>{formatNumber(getPriceData("18AYAR").Bid, "18AYAR")}</span>
              </div>
              <div className="col-4 p3">
                <span>{formatNumber(getPriceData("18AYAR").Ask, "18AYAR")}</span>
              </div>
            </div>
          )}
          {!show18Ayar && (
            <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
              <div className={`col-4 p3 h align-left`}>
                <span className="r">18</span> AYAR
              </div>
              <div className="col-4 p3 bg-light text-dark">
                <span>{formatNumber(getPriceData("18AYAR").Bid, "18AYAR")}</span>
              </div>
              <div className="col-4 p3"></div>
            </div>
          )}
          <div className="row boşluk"></div>
          {show14Ayar && (
            <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
              <div className={`col-4 p3 h align-left`}>
                <span className="r">14</span> AYAR
              </div>
              <div className="col-4 p3 bg-light text-dark">
                <span>{formatNumber(getPriceData("14AYAR").Bid, "14AYAR")}</span>
              </div>
              <div className="col-4 p3">
                <span>{formatNumber(getPriceData("14AYAR").Ask, "14AYAR")}</span>
              </div>
            </div>
          )}
          {!show14Ayar && (
            <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
              <div className={`col-4 p3 h align-left`}>
                <span className="r">14</span> AYAR
              </div>
              <div className="col-4 p3 bg-light text-dark">
                <span>{formatNumber(getPriceData("14AYAR").Bid, "14AYAR")}</span>
              </div>
              <div className="col-4 p3"></div>
            </div>
          )}
          <div className="row marquee-row">
            <div className="col-12 marquee-col">
              <Marquee text={marqueeText || "Varsayılan Kayan Yazı"} scrollAmount={scrollAmount || 30} />
            </div>
          </div>
          <div className="border-bottom başlık3 text-center d-flex align-items-center justify-content-between">
          </div>
        </div>
        <div className="col-sm-4 slayt-container">
          <Carousel />
        </div>
      </div>
      <div className="row başlık2 text-center d-flex align-items-center justify-content-between">
        <div className="col-sm border-right">
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-1 p3 d`}>Y</div>
            <div className={`col-3 p3 h align-left`}>ÇEYREK</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("YCEYREK").Bid, "YCEYREK")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("YCEYREK").Ask, "YCEYREK")}</span>
            </div>
          </div>
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-1 p3 d`}>E</div>
            <div className={`col-3 p3 h align-left`}>YARIM</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("YYARIM").Bid, "YYARIM")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("YYARIM").Ask, "YYARIM")}</span>
            </div>
          </div>
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-1 p3 d`}>N</div>
            <div className={`col-3 p3 h align-left`}>ZİYNET</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("YZIYNET").Bid, "YZIYNET")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("YZIYNET").Ask, "YZIYNET")}</span>
            </div>
          </div>
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-1 p3 d`}>İ</div>
            <div className={`col-3 p3 h align-left`}>ATA</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("YATA").Bid, "YATA")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("YATA").Ask, "YATA")}</span>
            </div>
          </div>
          <div className="row border-bottom başlık4 text-center d-flex align-items-center justify-content-between usd-bg">
            <div className={`col-4 p3 h`}>USD</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("USDTRY").Bid, "USDTRY")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("USDTRY").Ask, "USDTRY")}</span>
            </div>
          </div>
        </div>
        <div className="col-sm">
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-1 p3 d`}>E</div>
            <div className={`col-3 p3 h align-left`}>ÇEYREK</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("ECEYREK").Bid, "ECEYREK")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("ECEYREK").Ask, "ECEYREK")}</span>
            </div>
          </div>
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-1 p3 d`}>S</div>
            <div className={`col-3 p3 h align-left`}>YARIM</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("EYARIM").Bid, "EYARIM")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("EYARIM").Ask, "EYARIM")}</span>
            </div>
          </div>
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-1 p3 d`}>K</div>
            <div className={`col-3 p3 h align-left`}>ZİYNET</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("EZIYNET").Bid, "EZIYNET")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("EZIYNET").Ask, "EZIYNET")}</span>
            </div>
          </div>
          <div className="row border-bottom başlık2 text-center d-flex align-items-center justify-content-between">
            <div className={`col-1 p3 d`}>İ</div>
            <div className={`col-3 p3 h align-left`}>HAS</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("EHAS").Bid, "EHAS")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("EHAS").Ask, "EHAS")}</span>
            </div>
          </div>
          <div className="row border-bottom başlık4 text-center d-flex align-items-center justify-content-between euro-bg">
            <div className={`col-4 p3 h`}>EURO</div>
            <div className="col-4 p3 bg-light text-dark">
              <span>{formatNumber(getPriceData("EURTRY").Bid, "EURTRY")}</span>
            </div>
            <div className="col-4 p3">
              <span>{formatNumber(getPriceData("EURTRY").Ask, "EURTRY")}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Footer entegrasyonu */}
      <Footer symbols={symbols} />
    </div>
  );
};

export default PriceTable;
